import React, { useState, useEffect, useRef } from 'react';
import { useAIAgent } from '@/contexts/AIAgentContext';
import Icon from '@/components/AppIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { PulseBeamBorder } from '@/components/ui/PulseBeamBorder';


export default function AIAgentOverlay() {
    const {
        isActive,
        agentState,
        logs,
        agentMessage,
        deactivateAgent,
        cancelSession,
        pendingInput
    } = useAIAgent();

    const [isExpanded, setIsExpanded] = useState(false);
    const [userInput, setUserInput] = useState('');
    const messagesEndRef = useRef(null);

    // Auto-expand when active or input needed
    useEffect(() => {
        if (isActive && (pendingInput || agentState === 'input_needed')) {
            setIsExpanded(true);
        } else if (isActive && agentState === 'selecting') {
            setIsExpanded(true);
        }
    }, [isActive, pendingInput, agentState]);

    // Auto-scroll to bottom of logs
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [logs, agentMessage]);

    if (!isActive) return null;

    const handleSendInput = (e) => {
        e.preventDefault();
        if (!userInput.trim()) return;

        // If pending input handler exists, call it
        if (pendingInput?.onSubmit) {
            pendingInput.onSubmit(userInput);
        }

        setUserInput('');
    };

    return (
        <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end">
            <AnimatePresence>
                {isExpanded && (
                    <motion.div
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        className="mb-4 w-96 rounded-xl shadow-2xl overflow-hidden flex flex-col"
                        style={{ maxHeight: '600px' }}
                    >
                        <PulseBeamBorder className="bg-white dark:bg-neutral-900 rounded-xl h-full flex flex-col">
                            {/* Header */}
                            <div className="p-4 bg-gradient-to-r from-violet-600 to-indigo-600 flex items-center justify-between text-white rounded-t-xl">
                                <div className="flex items-center gap-2">
                                    <div className="p-1.5 bg-white/20 rounded-lg">
                                        <Icon name="Bot" size={20} />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-sm">GradOne AI Agent</h3>
                                        <p className="text-xs text-white/80 flex items-center gap-1">
                                            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                                            {agentState === 'selecting' ? 'Waiting for selection' :
                                                agentState === 'analyzing' ? 'Analyzing application' :
                                                    agentState === 'filling' ? 'Auto-filling form' :
                                                        agentState === 'input_needed' ? 'Waiting for input' :
                                                            'Active'}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex gap-1">
                                    <button
                                        onClick={() => setIsExpanded(false)}
                                        className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                                    >
                                        <Icon name="Minimize2" size={16} />
                                    </button>
                                    <button
                                        onClick={cancelSession}
                                        className="p-1.5 hover:bg-white/20 rounded-lg transition-colors text-red-200 hover:text-red-100"
                                    >
                                        <Icon name="X" size={16} />
                                    </button>
                                </div>
                            </div>

                            {/* Chat Area */}
                            <div className="flex-1 p-4 overflow-y-auto bg-neutral-50 dark:bg-neutral-950/50 min-h-[300px]">
                                <div className="space-y-4">
                                    {/* Introduction / Main Status Message */}
                                    {agentMessage && (
                                        <div className="flex gap-3">
                                            <div className="w-8 h-8 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center flex-shrink-0 text-violet-600 dark:text-violet-400">
                                                <Icon name="Bot" size={16} />
                                            </div>
                                            <div className="bg-white dark:bg-neutral-800 p-3 rounded-2xl rounded-tl-none shadow-sm border border-neutral-100 dark:border-neutral-700 text-sm">
                                                {agentMessage}
                                            </div>
                                        </div>
                                    )}

                                    {/* Logs / History */}
                                    {logs.filter(l => l.type !== 'debug').map((log) => (
                                        <div key={log.id} className={`flex gap-3 ${log.type === 'user' ? 'flex-row-reverse' : ''}`}>
                                            {log.type !== 'user' && (
                                                <div className="w-6 h-6 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center flex-shrink-0 text-neutral-500">
                                                    <Icon name={log.type === 'success' ? 'Check' : 'Activity'} size={12} />
                                                </div>
                                            )}
                                            <div className={`
                      text-xs py-2 px-3 rounded-lg 
                      ${log.type === 'user'
                                                    ? 'bg-violet-600 text-white rounded-tr-none'
                                                    : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 rounded-tl-none'}
                    `}>
                                                {log.message}
                                            </div>
                                        </div>
                                    ))}

                                    <div ref={messagesEndRef} />
                                </div>
                            </div>

                            {/* Input Area */}
                            <div className="p-4 border-t border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 rounded-b-xl">
                                <form onSubmit={handleSendInput} className="flex gap-2">
                                    <input
                                        type="text"
                                        value={userInput}
                                        onChange={(e) => setUserInput(e.target.value)}
                                        placeholder={pendingInput ? "Type your answer..." : "AI Agent is working..."}
                                        disabled={!pendingInput}
                                        className="flex-1 bg-neutral-100 dark:bg-neutral-800 border-none rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-violet-500"
                                    />
                                    <Button
                                        type="submit"
                                        disabled={!pendingInput || !userInput.trim()}
                                        size="sm"
                                        className="px-3"
                                    >
                                        <Icon name="Send" size={16} />
                                    </Button>
                                </form>

                                {/* Suggestions Grid */}
                                {pendingInput?.suggestions && (
                                    <div className="mt-3 grid grid-cols-1 gap-2 animate-in fade-in slide-in-from-bottom-2 duration-300">
                                        {pendingInput.suggestions.map((suggestion, idx) => (
                                            <button
                                                key={idx}
                                                onClick={() => {
                                                    if (suggestion.action) {
                                                        suggestion.action();
                                                    } else {
                                                        pendingInput.onSubmit(suggestion.label); // Default to submitting label
                                                    }
                                                }}
                                                className="text-left text-xs p-2.5 rounded-lg bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-900/40 border border-violet-100 dark:border-violet-800 transition-colors flex items-center justify-between group"
                                            >
                                                <span>{suggestion.label}</span>
                                                <Icon name="ArrowRight" size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </PulseBeamBorder>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Floating Toggle Button */}
            {!isExpanded && (
                <motion.button
                    layoutId="agent-trigger"
                    onClick={() => setIsExpanded(true)}
                    className="w-14 h-14 bg-gradient-to-r from-violet-600 to-indigo-600 rounded-full shadow-lg shadow-violet-500/30 flex items-center justify-center text-white hover:scale-105 transition-transform"
                >
                    <div className="relative">
                        <Icon name="Bot" size={28} />
                        <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 border-2 border-violet-600 rounded-full" />
                    </div>
                </motion.button>
            )}
        </div>
    );
}
